import * as yup from "yup";
import { InstrumentDetailsValidationSechma } from "../../common-for-log-sheet-and-run-logs/ValidationCommonSchema";
import DATA_MODEL_TABLE from "../../../../utils/constants/dataModelTable";
import { changeDateFormat } from "../../helpers";
import { isValid as isValidDate } from "date-fns";
import { RequiredStringSchema } from "yup/lib/string";
import { AnyObject } from "yup/lib/types";
const getCustomNullable = (schema: RequiredStringSchema<string | undefined, AnyObject>, changeTo = "") =>
  schema.nullable().transform((value) => value ?? changeTo);
export const logSheetValidationSchemaFactory = () => {
  return yup.object().shape(
    {
      actionDate: yup.date().typeError("Enter action date").required("Enter correct action date").nullable(),
      actionTime: yup
        .string()
        .typeError("Enter action Time")
        .required("Enter correct action Time")
        .test("maxtimeValidation", "Time should not be after maximal time", (value, obj) => {
          if (!value) {
            return true;
          }

          let actionDateObj = obj?.parent?.actionDate;
          let currentDateTime = new Date();
          if (!actionDateObj) {
            return true;
          }

          if (typeof actionDateObj === "string") {
            actionDateObj = new Date(obj?.parent?.actionDate);
          }

          if (!isValidDate(actionDateObj)) {
            return true;
          }

          if (changeDateFormat(actionDateObj) === changeDateFormat(currentDateTime)) {
            let currentTime = `${currentDateTime?.getHours()}:${currentDateTime?.getMinutes()}:${currentDateTime?.getSeconds()}`;
            return value! <= currentTime;
          }
          return true;
        }),
      systemStatus: yup
        .object({
          shouldBePublished: yup
            .boolean()
            .required(),
          newValue: yup
            .object({
              key: yup
                .string(),
              value: yup
                .string(),
            })
            .required(`Enter ${ DATA_MODEL_TABLE?.systemStatus?.value }`)
        }),
      gxpReady: yup
        .object({
          shouldBePublished: yup
            .boolean()
            .required(),
          newValue: yup
            .object({
              key: yup
                .string(),
              value: yup
                .string(),
            })
            .required(`Enter ${ DATA_MODEL_TABLE?.gxpReady?.value }`)
        }),
      action: yup.string().typeError("Enter action type").required("Enter action type").nullable(),
      externalDocument: yup
        .array(
          yup.object({
            link: yup.string().typeError("Enter valid URL"),
            name: yup.string().typeError("Enter Description")
          })
        )
        .nullable(),
      ecrNumber: yup.string().typeError("Enter ECR number").nullable(),
      logStatus: yup.string().typeError("Wrong type").nullable(),
      modelVersion: yup.string().typeError("Wrong type").nullable(),
      site: yup.string(),
      approverId: yup.string(),
      approverUserName: yup.string(),
      id: yup.string(),
      logSheetEntryId: yup.string(),
      materialNumber: yup.string().nullable(),
      description: getCustomNullable(yup.string().typeError("Enter description").required("Enter description")),
      operatorId: yup.string().nullable(),
      operatorUserName: yup.string().typeError("Enter operator User Name").nullable(),
      serialNumber: yup.string().when("equipmentId", {
        is: (equipmentId: string) => !equipmentId,
        then: yup.string().typeError("Enter serial number").nullable().required(""),
        otherwise: yup.string().typeError("Enter serial number").nullable()
      }),
      equipmentId: yup.string().when("serialNumber", {
        is: (serialNumber: string) => !serialNumber,
        then: yup.string().typeError("Enter Equipment ID").nullable().required(""),
        otherwise: yup.string().typeError("Enter Equipment ID").nullable()
      }),
      equipmentModel: yup.string(),
      equipmentNickName: yup.string(),
      inventoryId: yup.string().required(),
      updatedSoftwareVersion: yup.object({
        value: yup.string().typeError("EnterNew software version").nullable(),
        shouldBePublished: yup.boolean().typeError("Enter publish to equipment repository").nullable()
      }),
      ...InstrumentDetailsValidationSechma
    },
    [["serialNumber", "equipmentId"]]
  );
};

/**
 * Creates a Yup validation schema for status-like objects (e.g., systemStatus, gxpReady).
 * It ensures that either the top-level key/value or the nested newValue.key/value is provided.
 * @param fieldName The display name of the field for the error message.
 */
const createStatusValidationSchema = (fieldName: string) => {
  return yup
    .object()
    .shape({
      key: yup.string().nullable(),
      value: yup.string().nullable(),
      newValue: yup
        .object()
        .shape({
          key: yup.string(),
          value: yup.string(),
        })
        .nullable(),
      shouldBePublished: yup.boolean(),
    })
    .test(
      "status-required",
      `Enter ${fieldName}`,
      (status) => {
        if (!status) return false;

        const hasCurrentStatus = !!(status.key && status.value && status.key !== "-1");

        // Valid if the nested newValue object and its key/value pair exist
        const hasNewStatus = !!(status.newValue && status.newValue.key && status.newValue.value);

        return hasCurrentStatus || hasNewStatus;
      }
    )
    .required(`Enter ${fieldName}`);
};


export const logSheetClusterValidationSchemaFactory = () => {
  return yup.object().shape(
    {
      systemStatusCluster: createStatusValidationSchema(DATA_MODEL_TABLE?.systemStatus?.value),
      actionDate: yup.date().typeError("Enter action date").required("Enter correct action date"),
      actionTime: yup
        .string()
        .typeError("Enter action time")
        .required("Enter correct action time")
        .test("maxtimeValidation", "Time should not be after maximal time", (value, obj) => {
          if (!value) {
            return true;
          }

          let actionDateObj = obj?.parent?.actionDate;
          let currentDateTime = new Date();
          if (!actionDateObj) {
            return true;
          }

          if (typeof actionDateObj === "string") {
            actionDateObj = new Date(obj?.parent?.actionDate);
          }

          if (!isValidDate(actionDateObj)) {
            return true;
          }

          if (changeDateFormat(actionDateObj) === changeDateFormat(currentDateTime)) {
            let currentTime = `${currentDateTime?.getHours()}:${currentDateTime?.getMinutes()}:${currentDateTime?.getSeconds()}`;
            return value! <= currentTime;
          }

          return true;
        }),
      gxpReadyCluster: createStatusValidationSchema(DATA_MODEL_TABLE?.gxpReady?.value),
      externalDocument: yup
        .array(
          yup.object({
            link: yup.string().typeError("Enter valid URL"),
            name: yup.string().typeError("Enter Description")
          })
        )
        .nullable(),
      action: yup.string().typeError("Enter action type").required("Enter action type"),
      ecrNumber: yup.string().typeError("Enter ECR number").nullable(),
      modelVersion: yup.string().typeError("Type error").nullable(),
      site: yup.string(),
      approverId: yup.string(),
      approverUserName: yup.string(),
      id: yup.string(),
      logSheetEntryId: yup.string(),
      materialNumber: yup.string().nullable(),
      description: yup.string().typeError("Enter description").required("Enter description"),
      operatorId: yup.string().nullable(),
      operatorUserName: yup.string().typeError("Enter operator User Name").nullable(),
      serialNumber: yup.string().when("equipmentId", {
        is: (equipmentId: string) => !equipmentId,
        then: yup.string().typeError("Enter serial number").nullable(),
        otherwise: yup.string().typeError("Enter serial number").nullable()
      }),
      equipmentId: yup.string().when("serialNumber", {
        is: (serialNumber: string) => !serialNumber,
        then: yup.string().typeError("Enter Equipment ID").nullable(),
        otherwise: yup.string().typeError("Enter Equipment ID").nullable()
      }),
      equipmentModel: yup.string(),
      inventoryId: yup.string().required(),
      isClusterQualificationChanged: yup
        .boolean()
        .typeError("Enter publish to cluster qualification change")
        .nullable(),
      updatedSoftwareVersion: yup.object({
        value: yup.string().typeError("EnterNew software version").nullable(),
        shouldBePublished: yup.boolean().typeError("Enter publish to equipment repository").nullable()
      }),
      subEquipment: yup.array(),
      ...InstrumentDetailsValidationSechma
    },
    [["serialNumber", "equipmentId"]]
  );
};
